import { useState } from 'react';
import { Box, Button, Typography } from '@mui/material';
import readXlsxFile, { Email } from 'read-excel-file';
import { array, object, string, ZodError } from 'zod';
import moment from 'moment';
import { useToast } from '../../components/common/ToasterContext';
import UploadMembersComponent from '../../components/Member/UploadMembers';
import { FormValues, NewMember } from '../../components/Member/UploadMembers/types';
import getAPIUrl from '../../config';
import { parseError, postApiWithAuth } from '../../utils/apiCalls';
import { UploadMembersContainerProps } from './types';
import { dateSchema } from '../../utils/schemas';
import { validPhoneNumber, validZipCode } from '../../utils/helpers';
import Popup from '../../components/common/Popup/Popup';
import styles from './styles';

const excelValidationSchema = {
    FirstName: {
        prop: 'first_name',
        type: String,
        required: true,
    },
    LastName: {
        prop: 'last_name',
        type: String,
        required: true,
    },
    DOB: {
        prop: 'date_of_birth',
        type: Date,
        required: false,
        validate: (value: any) => {
            const isNotFutureDate = moment(value).isSameOrBefore(moment());
            if (!isNotFutureDate) {
                throw new Error('invalid');
            }
            return isNotFutureDate;
        },
    },
    Email: {
        prop: 'email',
        type: Email,
        required: true,
    },
    'Member ID': {
        prop: 'member_id',
        type: String,
        required: false,
    },
    Phone: {
        prop: 'phone',
        required: true,
        type: String,
        validate: (value: any) => {
            const number = validPhoneNumber(value);
            if (!number) {
                throw new Error('invalid');
            }
            return number;
        },
    },
    Street: {
        prop: 'address_1',
        type: String,
        required: true,
    },
    'Unit Number': {
        prop: 'unit_number',
        type: String,
        required: false,
    },
    City: {
        prop: 'city',
        type: String,
        required: true,
    },
    State: {
        prop: 'stateregion',
        type: String,
        required: true,
        validate: (value: any) => {
            if (value?.length !== 2) {
                throw new Error('invalid');
            }
            return value;
        },
    },
    Zip: {
        prop: 'postal_code',
        type: String,
        required: true,
        validate: (value: any) => {
            const code = validZipCode(value);
            if (!code) {
                throw new Error('invalid');
            }
            return code;
        },
    },
};

const memberRow = {
    first_name: string().nonempty(),
    last_name: string().nonempty(),
    email: string().email(),
    member_id: string().optional(),
    date_of_birth: dateSchema.optional(),
    phone: string().nonempty().regex(/^\d+$/, 'Only numbers are allowed').length(10, 'Please enter 10 digit number'),
    address_1: string(),
    unit_number: string().optional(),
    city: string(),
    stateregion: string(),
    postal_code: string().regex(/^\d+$/, 'Only numbers are allowed').length(5, 'Please enter only 5 digit number'),
};

const UploadMembers = ({
    uploadMembers,
    setUploadMembers,
    setReload,
    fromSelectedPartner,
    partnerData,
    selectedPartner,
    reload,
}: UploadMembersContainerProps) => {
    const [formValues, setFormValues] = useState<FormValues>({
        data: [],
        needs_welcome_kit: '1',
        selectedPartnerId:
            (fromSelectedPartner ? selectedPartner?.uniqueId : partnerData[0]?.uniqueId) || partnerData[0]?.uniqueId,
    });
    const toast = useToast();
    const [formErrors, setFormErrors] = useState<{
        [key: string]: Array<string>;
    }>();

    const createMemberValidationSchema = object({
        data: array(object(memberRow).strip()).nonempty({ message: 'Please upload valid excel sheet file.' }),
        needs_welcome_kit: string().nonempty({ message: 'Please select need of welcome kit.' }),
        selectedPartnerId: string().nonempty({ message: 'Partner should not be empty.' }),
    });

    const [validationErrors, setValidationErrors] = useState([]);
    const [existingMembers, setExistingMembers] = useState([]);
    const [newPatientCount, setNewPatientsCount] = useState(0);
    const [showDuplicateFilePopup, setShowDuplicateFilePopup] = useState(false);
    const [duplicateData, setDuplicateData] = useState<Array<number>>([]);
    const [submitting, setSubmitting] = useState<boolean>(false);

    const onSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
        e.preventDefault();
        try {
            setSubmitting(true);
            createMemberValidationSchema.parse(formValues);
            const res = await postApiWithAuth(
                `${getAPIUrl()}/employers/create-member/${formValues.selectedPartnerId}`,
                formValues.data,
                {
                    needs_welcome_kit: formValues.needs_welcome_kit,
                    uploadType: 'multiple',
                },
            );

            if (res.data.data?.existingPatients?.length) {
                setExistingMembers(res.data.data?.existingPatients);
            } else {
                setNewPatientsCount(res.data.data?.newPatients?.length);
            }
        } catch (error: any) {
            if (error instanceof ZodError) {
                setFormErrors(error.flatten().fieldErrors);
            } else if (toast) {
                if (error?.response?.status === 409) {
                    setShowDuplicateFilePopup(true);
                } else {
                    const err = parseError(error);
                    if (err) {
                        toast.onToggleSnackbar();
                        toast.onMessage(err);
                        toast.onError();
                    }
                }
            }
        } finally {
            setSubmitting(false);
        }
    };

    const onKitNeedChange = (value: string) => {
        setFormValues({
            ...formValues,
            needs_welcome_kit: value,
        });
    };

    const handleCloseUploadPop = () => {
        setFormValues({
            data: [],
            needs_welcome_kit: '1',
            selectedPartnerId: '',
        });
        setFormErrors(undefined);
        setValidationErrors([]);
        setExistingMembers([]);
        setDuplicateData([]);
        setNewPatientsCount(0);
        setShowDuplicateFilePopup(false);
        setUploadMembers(false);
    };

    function groupInvalidRows(a: any[]) {
        const result = a.reduce((r, n) => {
            const lastSubArray = r[r.length - 1];

            if (!lastSubArray || lastSubArray[lastSubArray.length - 1] !== n - 1) {
                r.push([]);
            }

            r[r.length - 1].push(n);
            return r;
        }, []);

        return result;
    }

    const validateExcelSheet = (file: File) => {
        readXlsxFile(file, {
            schema: excelValidationSchema,
            transformData(data) {
                return data.map((row: any) => {
                    if (row[11] === 'Leave Blank') {
                        const modifiedRow = row;
                        modifiedRow[11] = '';
                        return modifiedRow;
                    }
                    return row;
                });
            },
        })
            .then((rowsData) => {
                if (!rowsData.errors.length) {
                    const newMemberRows = rowsData.rows as Array<NewMember>;
                    const duplicateEmailsRows: number[] = [];
                    const filteredMembersRows = newMemberRows.filter((p, index, self) => {
                        const isNotDuplicate = self.findIndex((pt) => pt.email === p.email) === index;
                        if (!isNotDuplicate) duplicateEmailsRows.push(index + 2);
                        return isNotDuplicate;
                    });
                    if (duplicateEmailsRows.length) {
                        setFormValues({
                            ...formValues,
                            data: [],
                        });
                        setDuplicateData(groupInvalidRows(duplicateEmailsRows));
                    } else {
                        setFormValues({
                            ...formValues,
                            data: filteredMembersRows,
                        });
                    }
                } else {
                    setFormValues({
                        ...formValues,
                        data: [],
                    });
                    const rowNumbers = rowsData.errors
                        .map((e) => e.row)
                        .filter((item, index, self) => self.indexOf(item) === index);

                    setValidationErrors(groupInvalidRows(rowNumbers));
                }
            })
            .catch((error) => {
                if (toast) {
                    toast.onToggleSnackbar();
                    toast.onMessage(error);
                    toast.onError();
                }
            });
    };

    const handleTryAgain = () => {
        setValidationErrors([]);
    };

    const handleDuplicateFileTryAgain = () => {
        setShowDuplicateFilePopup(false);
    };

    const handleDuplicateDataTryAgain = () => {
        setDuplicateData([]);
    };

    const handleDuplicateTryAgain = () => {
        setExistingMembers([]);
    };

    return (
        <>
            <UploadMembersComponent
                open={uploadMembers}
                onKitNeedChange={onKitNeedChange}
                validateExcelSheet={validateExcelSheet}
                handleSubmit={onSubmit}
                handleCloseUploadPop={handleCloseUploadPop}
                fromSelectedPartner={fromSelectedPartner}
                formValues={formValues}
                formErrors={formErrors}
                partnerData={partnerData}
                defaultSelectedPartner={fromSelectedPartner ? selectedPartner : partnerData[0]}
                setFormValues={setFormValues}
                submitting={submitting}
            />
            {validationErrors.length > 0 && (
                <Popup
                    id="invalidData"
                    open={validationErrors.length > 0}
                    title="Unable to Import"
                    textAlign="left"
                    handleClose={() => null}
                    description={
                        <>
                            The following Rows were not imported due to missing required fields and/or incorrect format.
                        </>
                    }
                >
                    <Box sx={styles.errorsDataContainer}>
                        {validationErrors.map((errorRows: any) => {
                            return (
                                <Typography key={errorRows[0]} sx={styles.errorRows}>{`Rows ${errorRows[0]} 
                                    ${errorRows.length > 1 ? `- ${errorRows[errorRows.length - 1]}` : ''}`}</Typography>
                            );
                        })}
                        <Box sx={styles.popupButtonsContainer}>
                            <Button id="exit" variant="outlined" sx={styles.exit} onClick={handleCloseUploadPop}>
                                Exit
                            </Button>
                            <Button id="tryAgain" variant="contained" sx={styles.tryAgain} onClick={handleTryAgain}>
                                Try Again
                            </Button>
                        </Box>
                    </Box>
                </Popup>
            )}
            {duplicateData.length > 0 && (
                <Popup
                    id="duplicateData"
                    open={duplicateData.length > 0}
                    title="Unable to Import"
                    textAlign="left"
                    handleClose={() => null}
                    description={<>The following Rows were unable to be imported due to duplicate data.</>}
                >
                    <Box sx={styles.errorsDataContainer}>
                        {duplicateData.map((duplicateRows: any) => {
                            return (
                                <Typography key={duplicateRows[0]} sx={styles.errorRows}>{`Rows ${duplicateRows[0]} 
                                    ${
                                        duplicateRows.length > 1 ? `- ${duplicateRows[duplicateRows.length - 1]}` : ''
                                    }`}</Typography>
                            );
                        })}
                        <Box sx={styles.popupButtonsContainer}>
                            <Button id="exit" variant="outlined" sx={styles.exit} onClick={handleCloseUploadPop}>
                                Exit
                            </Button>
                            <Button
                                id="tryAgain"
                                variant="contained"
                                sx={styles.tryAgain}
                                onClick={handleDuplicateDataTryAgain}
                            >
                                Try Again
                            </Button>
                        </Box>
                    </Box>
                </Popup>
            )}
            {showDuplicateFilePopup && (
                <Popup
                    id="duplicateFile"
                    open={showDuplicateFilePopup}
                    title="Accounts In Use"
                    textAlign="left"
                    handleClose={() => null}
                    description={<>This file seems to be a duplicate, all members in this file currently exist.</>}
                >
                    <Box sx={styles.duplicatePopupButtonsContainer}>
                        <Button id="exit" variant="outlined" sx={styles.exit} onClick={handleCloseUploadPop}>
                            Exit
                        </Button>
                        <Button
                            id="tryAgain"
                            variant="contained"
                            sx={styles.tryAgain}
                            onClick={handleDuplicateFileTryAgain}
                        >
                            Try Again
                        </Button>
                    </Box>
                </Popup>
            )}
            {existingMembers.length > 0 && (
                <Popup
                    id="existingMembers"
                    open={existingMembers.length > 0}
                    title="Accounts In Use"
                    textAlign="left"
                    handleClose={() => null}
                    description={<>The following member emails are already in use:</>}
                >
                    <Box sx={styles.errorsDataContainer}>
                        {existingMembers.map((email: string) => {
                            return (
                                <Typography key={email} sx={styles.email}>
                                    {email}
                                </Typography>
                            );
                        })}
                        <Box sx={styles.popupButtonsContainer}>
                            <Button id="exit" variant="outlined" sx={styles.exit} onClick={handleCloseUploadPop}>
                                Exit
                            </Button>
                            <Button
                                id="tryAgain"
                                variant="contained"
                                sx={styles.tryAgain}
                                onClick={handleDuplicateTryAgain}
                            >
                                Try Again
                            </Button>
                        </Box>
                    </Box>
                </Popup>
            )}
            {existingMembers.length <= 0 && newPatientCount > 0 && (
                <Popup
                    id="success"
                    open={existingMembers.length <= 0 && newPatientCount > 0}
                    title="Successful Upload!"
                    textAlign="left"
                    handleClose={() => null}
                    description={
                        <>{`${newPatientCount} Members were successfully added to ${
                            fromSelectedPartner ? selectedPartner?.name : partnerData[0]?.name
                        }. These members will be notified to sign up via email. `}</>
                    }
                >
                    <>
                        <Button
                            id="view"
                            variant="contained"
                            sx={styles.view}
                            onClick={() => {
                                if (setReload) {
                                    setReload(!reload);
                                }
                                handleCloseUploadPop();
                            }}
                        >
                            View
                        </Button>
                    </>
                </Popup>
            )}
        </>
    );
};

export default UploadMembers;
